import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/context';
import { useToast } from '../../context/ToastContext';
import { useLanguage } from '../../context/LanguageContext';
import BtnPrimaryL from '../Buttons/BtnPrimaryL';
import BtnSecondaryL from '../Buttons/BtnSecondaryL';

const NativeAuthButtons = () => {
  const { copy } = useLanguage();
  const auth = useAuthContext();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);

      // Dynamic import for Capacitor plugins (only works in native apps)
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithGoogle();

      if (result.user) {
        // Send the Firebase user data to your backend
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            firebaseUser: result.user,
            idToken: result.user.getIdToken(),
          }),
        });

        if (!response.ok) {
          throw new Error('Google authentication failed');
        }

        const data = await response.json();

        // CRITICAL: Store the auth token if provided
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }

        // Update auth context
        auth.setUserData(data.userData);
        auth.setAuthStatus('success');
        auth.setLoggedIn(true);

        // Fetch complete user data with all nested information
        try {
          await auth.refetchUserData();
        } catch (refetchError) {
          console.error("Error refetching user data:", refetchError);
          // Continue anyway - we have basic userData
        }

        // Navigate to home
        navigate('/');
        showToast('Successfully signed in with Google', 'success');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      showToast(error.message || 'Failed to sign in with Google', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setIsLoading(true);

      // Dynamic import for Capacitor plugins (only works in native apps)
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const result = await SignInWithApple.authorize({
        requestedScopes: [
          SignInWithApple.Scope.Email,
          SignInWithApple.Scope.FullName,
        ],
      });

      if (result.response && result.response.identityToken) {
        // Send the Apple user data to your backend
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/apple`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            identityToken: result.response.identityToken,
            authorizationCode: result.response.authorizationCode,
            user: result.response.user,
          }),
        });

        if (!response.ok) {
          throw new Error('Apple authentication failed');
        }

        const data = await response.json();

        // CRITICAL: Store the auth token if provided
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }

        // Update auth context
        auth.setUserData(data.userData);
        auth.setAuthStatus('success');
        auth.setLoggedIn(true);

        // Fetch complete user data with all nested information
        try {
          await auth.refetchUserData();
        } catch (refetchError) {
          console.error("Error refetching user data:", refetchError);
          // Continue anyway - we have basic userData
        }

        // Navigate to home
        navigate('/');
        showToast('Successfully signed in with Apple', 'success');
      }
    } catch (error) {
      console.error('Apple sign-in error:', error);
      showToast(error.message || 'Failed to sign in with Apple', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Google Sign In */}
      <BtnPrimaryL
        buttonCTA={copy.login.google_cta || "Continue with Google"}
        onClickLink={handleGoogleSignIn}
        disabled={isLoading}
        className="w-full"
      />
      
      {/* Apple Sign In */}
      <BtnSecondaryL
        buttonCTA={copy.login.apple_cta || "Continue with Apple"}
        onClickLink={handleAppleSignIn}
        disabled={isLoading}
        className="w-full"
      />
    </div>
  );
};

export default NativeAuthButtons;
